<template>
  <layout :title="pageTitle">
    <section class="section">
      <div class="columns">
        <div class="column is-3">
          <ul class="inner-menu box">
            <li>
              <ScrollLink to="#overview">Overview</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#questionSets">Question Sets</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#projects">Projects</ScrollLink>
            </li>
            <li>
              <a href="#defaults">Edit Defaults</a>
            </li>
            <li class="submenu">
              <ScrollLink to="#defaultsDetails">Details</ScrollLink>
              <ScrollLink to="#reports">Reports</ScrollLink>
              <ScrollLink to="#welcomes">Welcomes</ScrollLink>
              <ScrollLink to="#emails">Emails</ScrollLink>
              <ScrollLink to="#relationships">Relationships</ScrollLink>
            </li>
            <li>
              <ScrollLink to="#actions">Actions</ScrollLink>
            </li>
          </ul>
        </div>
        <div class="column is-9" id="mainbody">
          <div class="box" id="overview">
            <a id="overview"></a>
            <h3>Overview</h3>
            <hr style="margin-top: 15px" />
            <form @submit.prevent="submit" v-on:change="submit">
              <div class="columns">
                <div class="column">
                  <b-field label="Client Name">
                    <b-input v-model="form.name"></b-input>
                  </b-field>
                </div>
              </div>
              <div class="columns">
                <div class="column">
                  <b-field label="Description">
                    <b-input v-model="form.description"></b-input>
                  </b-field>
                </div>
              </div>
            </form>
          </div>

          <!-- question sets -->
          <div class="columns">
            <div class="column">
              <div class="box">
                <a class="anchor" id="questionSets"></a>
                <h3>Question Sets</h3>
                <hr />
                <p class="mb-2"><b>Linked Question Sets</b></p>
                <p v-if="client.question_sets.length == 0">
                  No question sets have been linked
                </p>
                <div
                  :key="questionSet.id"
                  class="client mb-2"
                  v-for="questionSet in client.question_sets"
                  style="display: flex; justify-content: space-between"
                >
                  {{ questionSet.name }}
                  <div
                    class="icon-container"
                    style="cursor: pointer"
                    @click="detachQuestionSet(questionSet)"
                  >
                    <b-icon
                      pack="fas"
                      icon="trash"
                      type="is-danger"
                      size="is-small"
                    ></b-icon>
                  </div>
                </div>
                <hr />
                <b-field v-if="questionSets.length > 0">
                  <b-select
                    placeholder="Link a question set"
                    v-model="selectedQuestionSet"
                    expanded
                    @input="attachQuestionSet()"
                  >
                    <option
                      :key="questionSet.id"
                      v-for="questionSet in questionSets"
                      :value="questionSet"
                    >
                      {{ questionSet.name }}
                    </option>
                  </b-select>
                </b-field>
              </div>
            </div>
          </div>

          <!-- projects -->
          <div class="box">
            <a id="projects"></a>
            <div class="title-bar">
              <h3>Projects</h3>
              <div class="buttons">
                <inertia-link
                  :href="route('projects.create', { client: client.unique_id })"
                >
                  <b-button class="is-primary"> Add </b-button>
                </inertia-link>
              </div>
            </div>
            <hr style="margin-top: 15px" />
            <div v-if="client.projects.length">
              <ViewProjectsTable :projects="client.projects" />
            </div>
            <div class="empty-box" v-else>
              <div class="icon">
                <b-icon pack="fas" icon="rocket" size="is-small"></b-icon>
              </div>
              <p>There are no projects under this client</p>
              <inertia-link
                :href="route('projects.create', { client: client.unique_id })"
              >
                <b-button class="is-primary"> Create one now </b-button>
              </inertia-link>
            </div>
          </div>

          <ManageDefaults
            :defaults="defaults"
            :entity="client"
            :relationships="client.relationships"
            entityType="App\Models\Client"
          />

          <div class="box">
            <a id="actions"></a>
            <div class="title-bar">
              <h3>Actions</h3>
            </div>
            <hr style="margin-top: 15px" />
            <div class="buttons">
              <b-button @click="showDeleteModal = true" class="is-danger"
                >Delete Client</b-button
              >
              <inertia-link
                :href="route('duplicate.client', [client.unique_id])"
              >
                <b-button class="is-info"> Clone </b-button>
              </inertia-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- modals -->
    <b-modal
      v-model="showDeleteModal"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      aria-role="dialog"
      aria-modal
    >
      <DeleteModal
        :delete-route="route('clients.destroy', client.unique_id).toString()"
        :name="client.name"
        :message="'Deleting this client will also delete all projects and project groups associated with this client.'"
      />
    </b-modal>
  </layout>
</template>

<script>
import Layout from './Layout';
import ScrollLink from '@/Components/Shared/ScrollLink';
import ManageDefaults from '../../Components/Management/ManageDefaults';
import ViewProjectsTable from '../../Components/Management/ViewProjectsTable';
import DeleteModal from '../../Components/Management/DeleteModal';
import viewQuestionSetsTable from '../../Components/Management/ViewQuestionSetsTable';

export default {
  components: {
    Layout,
    ScrollLink,
    ManageDefaults,
    ViewProjectsTable,
    DeleteModal,
    viewQuestionSetsTable,
  },
  props: {
    client: { type: Object },
    settings: { type: Object },
    defaults: { type: Object },
    questionSets: { type: Array },
  },
  data() {
    return {
      pageTitle: 'Edit Client: ' + this.client.name,
      form: {
        name: this.client.name,
        description: this.client.description,
        project_limit: this.settings['projectLimit'].value,
        active_project_limit: this.settings['activeProjectLimit'].value,
        isSending: false,
      },
      showDeleteModal: false,
      selectedQuestionSet: null,
    };
  },
  methods: {
    attachQuestionSet: function () {
      const data = {
        questionSet: this.selectedQuestionSet,
      };
      let self = this;
      this.$inertia.patch(
        route('clients.attachQuestionSet', this.client.unique_id),
        data,
        {
          preserveScroll: true,
          onFinish: function () {
            self.selectedQuestionSet = null;
          },
        }
      );
    },
    detachQuestionSet: function (questionSet) {
      const data = {
        questionSet: questionSet,
      };
      this.$inertia.patch(
        route('clients.detachQuestionSet', this.client.unique_id),
        data,
        {
          preserveScroll: true,
        }
      );
    },
    submit() {
      const data = {
        name: this.form.name,
        description: this.form.description,
        project_limit: this.form.project_limit,
        active_project_limit: this.form.active_project_limit,
      };
      this.$inertia.patch(
        route('clients.update', this.client.unique_id),
        data,
        {
          onStart: () => (this.form.isSending = true),
          onFinish: () => {
            if (Object.keys(this.errors).length) {
              this.$buefy.toast.open({
                message: 'There was an error updating the client',
                type: 'is-danger',
                position: 'is-top',
              });
            } else {
              this.$buefy.toast.open({
                message: 'Client information updated',
                type: 'is-success',
                position: 'is-top',
              });
            }
            this.form.isSending = false;
          },
          onError: () => {},
        }
      );
    },
  },
};
</script>
